// src/api/googleDrive.js
// Google Drive API Service with OAuth for document management

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_CLIENT_SECRET = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;

// Auto-detect localhost for development - always use current origin for redirect
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const GOOGLE_REDIRECT_URI = isLocalhost
  ? `${window.location.origin}/oauth/callback`
  : (process.env.REACT_APP_GOOGLE_REDIRECT_URI || `${window.location.origin}/oauth/callback`);

// Scopes needed for Google Drive, Docs, Sheets, Calendar and Gmail
const SCOPES = [
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/documents',
  'https://www.googleapis.com/auth/drive',
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/gmail.readonly'
].join(' ');

// Token storage keys
const TOKEN_KEY = 'google_drive_tokens';
const TOKEN_EXPIRY_KEY = 'google_drive_token_expiry';

/**
 * Get stored tokens from localStorage
 */
export const getStoredTokens = () => {
  const tokens = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!tokens) return null;

  const parsedTokens = JSON.parse(tokens);
  const expiryTime = expiry ? parseInt(expiry) : 0;

  // Check if token is expired (with 5 minute buffer)
  if (Date.now() > expiryTime - 300000) {
    // Token expired or about to expire, try to refresh
    if (parsedTokens.refresh_token) {
      return { ...parsedTokens, needsRefresh: true };
    }
    return null;
  }

  return parsedTokens;
};

/**
 * Store tokens in localStorage
 */
export const storeTokens = (tokens) => {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));

  // Calculate expiry time (usually 1 hour from now)
  const expiryTime = Date.now() + (tokens.expires_in || 3600) * 1000;
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
};

/**
 * Clear stored tokens
 */
export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

/**
 * Check if user is authenticated with Google
 */
export const isGoogleAuthenticated = () => {
  const tokens = getStoredTokens();
  return tokens !== null && tokens.access_token;
};

/**
 * Generate OAuth URL for user authorization
 */
export const getGoogleAuthUrl = () => {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: GOOGLE_REDIRECT_URI,
    response_type: 'code',
    scope: SCOPES,
    access_type: 'offline',
    prompt: 'select_account consent',
    state: window.location.pathname // Store current path to redirect back
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
};

/**
 * Exchange authorization code for tokens
 */
export const exchangeCodeForTokens = async (code) => {
  try {
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code,
        client_id: GOOGLE_CLIENT_ID,
        client_secret: GOOGLE_CLIENT_SECRET,
        redirect_uri: GOOGLE_REDIRECT_URI,
        grant_type: 'authorization_code',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || 'Failed to exchange code for tokens');
    }

    const tokens = await response.json();
    storeTokens(tokens);

    return tokens;
  } catch (error) {
    console.error('Error exchanging code for tokens:', error);
    throw error;
  }
};

/**
 * Refresh access token using refresh token
 */
export const refreshAccessToken = async () => {
  const tokens = getStoredTokens();

  if (!tokens || !tokens.refresh_token) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        refresh_token: tokens.refresh_token,
        client_id: GOOGLE_CLIENT_ID,
        client_secret: GOOGLE_CLIENT_SECRET,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || 'Failed to refresh token');
    }

    const newTokens = await response.json();

    // Preserve refresh token if not returned
    if (!newTokens.refresh_token) {
      newTokens.refresh_token = tokens.refresh_token;
    }

    storeTokens(newTokens);
    return newTokens;
  } catch (error) {
    console.error('Error refreshing token:', error);
    clearTokens();
    throw error;
  }
};

/**
 * Get valid access token (refreshing if necessary)
 */
export const getValidAccessToken = async () => {
  let tokens = getStoredTokens();

  if (!tokens) {
    throw new Error('Not authenticated with Google');
  }

  if (tokens.needsRefresh) {
    tokens = await refreshAccessToken();
  }

  return tokens.access_token;
};

/**
 * Make authenticated request to Google API
 */
const googleApiRequest = async (url, options = {}) => {
  const accessToken = await getValidAccessToken();

  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Token might be invalid, try refreshing
    const newTokens = await refreshAccessToken();

    return fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${newTokens.access_token}`,
        ...options.headers,
      },
    });
  }

  return response;
};

/**
 * Copy a Google Doc template
 */
export const copyGoogleDoc = async (templateId, newTitle, folderId = null) => {
  try {
    const metadata = {
      name: newTitle,
    };

    if (folderId) {
      metadata.parents = [folderId];
    }

    const response = await googleApiRequest(
      `https://www.googleapis.com/drive/v3/files/${templateId}/copy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadata),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to copy document');
    }

    return await response.json();
  } catch (error) {
    console.error('Error copying Google Doc:', error);
    throw error;
  }
};

/**
 * Replace text in a Google Doc
 */
export const replaceTextInDoc = async (documentId, replacements) => {
  try {
    const requests = replacements.map(({ find, replace }) => ({
      replaceAllText: {
        containsText: {
          text: find,
          matchCase: true,
        },
        replaceText: replace || '',
      },
    }));

    console.log('Sending replacements to Google Docs API...');
    console.log('Sample replacements:', replacements.slice(0, 3));

    const response = await googleApiRequest(
      `https://docs.googleapis.com/v1/documents/${documentId}:batchUpdate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requests }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      console.error('Google Docs API error:', error);
      throw new Error(error.error?.message || 'Failed to replace text');
    }

    const result = await response.json();

    // Log how many replacements were actually made
    if (result.replies) {
      result.replies.forEach((reply, index) => {
        if (reply.replaceAllText) {
          const count = reply.replaceAllText.occurrencesChanged || 0;
          if (count > 0) {
            console.log(`✅ "${replacements[index]?.find}" → replaced ${count} time(s)`);
          } else {
            console.warn(`⚠️ "${replacements[index]?.find}" → NOT FOUND in document`);
          }
        }
      });
    }

    return result;
  } catch (error) {
    console.error('Error replacing text in doc:', error);
    throw error;
  }
};

/**
 * Export Google Doc to PDF
 */
export const exportDocToPdf = async (documentId) => {
  try {
    const response = await googleApiRequest(
      `https://www.googleapis.com/drive/v3/files/${documentId}/export?mimeType=application/pdf`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to export to PDF');
    }

    return await response.blob();
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw error;
  }
};

/**
 * Get Google Doc/Drive file info
 */
export const getFileInfo = async (fileId) => {
  try {
    const response = await googleApiRequest(
      `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,mimeType,webViewLink,webContentLink`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to get file info');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting file info:', error);
    throw error;
  }
};

/**
 * Move file to a folder
 */
export const moveFileToFolder = async (fileId, folderId) => {
  try {
    // First get current parents
    const fileInfo = await googleApiRequest(
      `https://www.googleapis.com/drive/v3/files/${fileId}?fields=parents`,
      { method: 'GET' }
    );

    const file = await fileInfo.json();
    const previousParents = file.parents ? file.parents.join(',') : '';

    const response = await googleApiRequest(
      `https://www.googleapis.com/drive/v3/files/${fileId}?addParents=${folderId}&removeParents=${previousParents}&fields=id,parents`,
      {
        method: 'PATCH',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to move file');
    }

    return await response.json();
  } catch (error) {
    console.error('Error moving file:', error);
    throw error;
  }
};

/**
 * Create a folder in Google Drive
 */
export const createFolder = async (folderName, parentFolderId = null) => {
  try {
    const metadata = {
      name: folderName,
      mimeType: 'application/vnd.google-apps.folder',
    };

    if (parentFolderId) {
      metadata.parents = [parentFolderId];
    }

    const response = await googleApiRequest(
      'https://www.googleapis.com/drive/v3/files',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadata),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to create folder');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating folder:', error);
    throw error;
  }
};

/**
 * List files in a folder
 */
export const listFilesInFolder = async (folderId, mimeType = null) => {
  try {
    let query = `'${folderId}' in parents and trashed = false`;
    if (mimeType) {
      query += ` and mimeType = '${mimeType}'`;
    }

    const response = await googleApiRequest(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,mimeType,webViewLink,createdTime)`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to list files');
    }

    const data = await response.json();
    return data.files || [];
  } catch (error) {
    console.error('Error listing files:', error);
    throw error;
  }
};

/**
 * Delete a file from Google Drive
 */
export const deleteFile = async (fileId) => {
  try {
    const response = await googleApiRequest(
      `https://www.googleapis.com/drive/v3/files/${fileId}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok && response.status !== 204) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to delete file');
    }

    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Read the full text content of a Google Doc (for template validation)
 */
export const getDocumentText = async (documentId) => {
  const response = await googleApiRequest(
    `https://docs.googleapis.com/v1/documents/${documentId}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to read document');
  }

  const doc = await response.json();
  let text = '';

  const extractText = (content) => {
    if (!content) return;
    for (const element of content) {
      if (element.paragraph?.elements) {
        for (const el of element.paragraph.elements) {
          if (el.textRun?.content) text += el.textRun.content;
        }
      }
      if (element.table?.tableRows) {
        for (const row of element.table.tableRows) {
          for (const cell of row.tableCells) {
            extractText(cell.content);
          }
        }
      }
    }
  };

  extractText(doc.body?.content);
  return text;
};

/**
 * Generate document from Google Docs template
 * This is the main function to use for document generation
 */
export const generateDocumentFromTemplate = async ({
  templateId,
  documentTitle,
  replacements,
  folderId = null,
  exportToPdf = true,
}) => {
  console.log('=== GOOGLE DRIVE DOCUMENT GENERATION ===');
  console.log('Template ID:', templateId);
  console.log('Document Title:', documentTitle);
  console.log('Replacements count:', replacements.length);

  try {
    // Step 1: Copy the template
    console.log('Step 1: Copying template...');
    const copiedDoc = await copyGoogleDoc(templateId, documentTitle, folderId);
    console.log('Template copied, new ID:', copiedDoc.id);

    // Step 2: Replace all text placeholders
    console.log('Step 2: Replacing placeholders...');
    await replaceTextInDoc(copiedDoc.id, replacements);
    console.log('Placeholders replaced');

    // Step 3: Get document info with links
    console.log('Step 3: Getting document info...');
    const docInfo = await getFileInfo(copiedDoc.id);
    console.log('Document URL:', docInfo.webViewLink);

    // Step 4: Export to PDF if requested
    let pdfBlob = null;
    let pdfUrl = null;

    if (exportToPdf) {
      console.log('Step 4: Exporting to PDF...');
      pdfBlob = await exportDocToPdf(copiedDoc.id);
      console.log('PDF exported, size:', pdfBlob.size);
    }

    console.log('=== DOCUMENT GENERATION COMPLETE ===');

    return {
      documentId: copiedDoc.id,
      documentUrl: docInfo.webViewLink,
      documentName: documentTitle,
      pdfBlob,
      pdfUrl,
    };
  } catch (error) {
    console.error('Error generating document from template:', error);
    throw error;
  }
};

export default {
  isGoogleAuthenticated,
  getGoogleAuthUrl,
  exchangeCodeForTokens,
  refreshAccessToken,
  getValidAccessToken,
  clearTokens,
  copyGoogleDoc,
  replaceTextInDoc,
  exportDocToPdf,
  getFileInfo,
  moveFileToFolder,
  createFolder,
  listFilesInFolder,
  deleteFile,
  generateDocumentFromTemplate,
};
